// A window: the one component every window on the page is made with. The
// drive's (drive-window.js), every window of the ?page=redesign mockup
// (js/redesign/) and the featured photos' (js/featured/). One markup, made
// here, and one look, css/drive.css's .win: the title bar (the file's name in
// tracked caps, a note at its right; or plain, nothing in it) over the
// window's body. Change how a window looks there and every window changes
// with it.
//
//   .win[data-kind][data-inactive]   (data-inactive: setActive)
//     .win__bar
//       .win__title     the file's name (none in a plain bar)
//       .win__meta      the note at the right (optional)
//       .win__max       the maximize button at the right end (optional)
//       .win__close     the close button after it (optional)
//     .win__body        the contents
import { h } from "./files.js";

/**
 * title       the file's name in the title bar: text, or nodes (the drive's
 *             LED and name); or null, a plain bar with nothing in it
 * label       a window with a plain bar: its name for assistive technology
 *             (it is a labelled group); required then, and only then
 * meta        the note at the right of the bar: text, a node, or null (none)
 * kind        what it shows (photo, text, video, files, ...): data-kind, for
 *             the stylesheets
 * body        its contents: a node, or an array of them
 * tag         the window's element: "div", or "section" where the window is
 *             a section of the page of its own (the drive's)
 * barTag      the title bar's element ("div"; "header" for a section's)
 * titleTag    the title's element ("span"; "h2" where it is the section's
 *             heading)
 * maximize    a small maximize button at the right end of the bar, or null
 *             (none, the default): { label, onClick }; label is its name for
 *             assistive technology ("Open photo full screen"), onClick what
 *             it does (it is a button: Enter and Space work)
 * close       a small close button at the very end of the bar, or null
 *             (none, the default): { label, onClick }, as maximize's
 * attrs       more attributes for the window (id, aria-*)
 * titleAttrs  more attributes for the title (id, for aria-labelledby)
 *
 * Returns { el, bar, title, body, maximize, close, setTitle }: the window and its
 * parts (title null in a plain bar; maximize null without one), for the
 * caller's own classes and contents. setTitle(text) writes (or, with null,
 * clears) a plain bar's title, for a page that changes what its bars say;
 * the window stays named by its label, the title being only for the eye.
 */
export function createWindow({ title, label = null, meta = null, kind, body = [], tag = "div", barTag = "div", titleTag = "span", maximize = null, close = null, attrs = {}, titleAttrs = {} }) {
  if (!kind) throw new TypeError("createWindow: a window needs a kind (what it shows)");
  if (title === "") throw new TypeError("createWindow: a title can't be empty (null: a plain bar)");
  if (title == null && !label) throw new TypeError("createWindow: a window with a plain bar (no title) needs a label");
  if (title != null && label) throw new TypeError("createWindow: a titled window is named by its title; a label is for a plain bar");
  const titleEl = title == null ? null : h(`${titleTag}.win__title`, typeof title === "string" ? { ...titleAttrs, text: title } : titleAttrs, typeof title === "string" ? null : title);
  const bar = h(`${barTag}.win__bar`, {}, titleEl, meta === null ? null : h("span.win__meta", typeof meta === "string" ? { text: meta } : {}, typeof meta === "string" ? null : meta));
  const max = maximize === null ? null : barButton("win__max", MAXIMIZE_ICON, maximize);
  const shut = close === null ? null : barButton("win__close", CLOSE_ICON, close);
  bar.append(...[max, shut].filter(Boolean));
  const bodyEl = h("div.win__body", {}, body);
  const named = label ? { role: "group", "aria-label": label } : {};
  const el = h(`${tag}.win`, { ...named, ...attrs, dataset: { kind } }, bar, bodyEl);
  function setTitle(text) {
    if (title != null) throw new TypeError("createWindow: setTitle is for a plain bar; this window has a title");
    const now = bar.querySelector(":scope > .win__title");
    if (text == null) now?.remove();
    else if (now) now.textContent = text;
    else bar.prepend(h(`${titleTag}.win__title`, { "aria-hidden": "true", text }));
  }
  return { el, bar, title: titleEl, body: bodyEl, maximize: max, close: shut, setTitle };
}

/** Marks a window active (the one in front, its bar as usual) or inactive
 * (behind one opened later: still there, its bar greyed, as an operating
 * system shows a window that isn't the active one). Windows are active
 * unless marked (data-inactive). */
export function setActive(win, on) {
  win.toggleAttribute("data-inactive", !on);
}

const MAXIMIZE_ICON = `<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M7 1h4v4M5 11H1V7M11 1 7.5 4.5M1 11l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>`;
const CLOSE_ICON = `<svg viewBox="0 0 12 12" width="9" height="9" aria-hidden="true" focusable="false"><path d="M2 2l8 8M10 2l-8 8" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>`;
// a small button of the bar's (maximize, close)
function barButton(cls, icon, { label, onClick } = {}) {
  if (!label || typeof onClick !== "function") throw new TypeError(`createWindow: ${cls} needs { label, onClick }`);
  const button = h(`button.${cls}`, { type: "button", "aria-label": label, title: label, html: icon });
  button.addEventListener("click", onClick);
  return button;
}
